/**
 * One monitored SNMP device: who it is, whether it answers, its latest readings, its performance
 * graphs, and - for a Synology - its identity and storage. Every setting is editable here.
 *
 * The port of `Views/MonitorDetailPage.xaml` + `MonitorDetailViewModel`. The performance card and the
 * metric groups are the same builders the device page's SNMP tab uses.
 *
 *   * A MONITOR WHOSE MACHINE ALSO RUNS THE AGENT says so and links to that device: the fleet lists it
 *     there, and without the banner the row dropping off "monitored devices" reads as it having gone.
 *   * THE EDIT PANEL CHANGES EVERYTHING WITHOUT THE SECRETS. Version, security level, protocols, the v3
 *     user, the port, the address, the kind, the poller, the Synology platform and web port all save
 *     with the stored community and passwords kept; only "Replace the SNMP passwords" (or "Replace the
 *     admin password") sends new ones. Requiring a password to change a port is how a wrong port stays
 *     wrong.
 *   * THE KIND SELECTS THE METRIC SET, which is how a printer the classifier guessed wrong starts
 *     reporting toner.
 *   * THE POLLER PICKER IS LEFT ON "KEEP" WHEN THE CURRENT ONE IS NOT LISTED - that is the retired-agent
 *     case the picker exists for, and silently landing on somebody else would hide it.
 *   * TEST PROBES THE FORM, NOT THE DATABASE - unsaved settings included, secrets filled in
 *     server-side - through the same request builder as Save, so a probe cannot pass on settings that
 *     then fail once stored. The verdict keeps its own lines, so the save's message does not wipe it.
 *   * Leaving the page releases the watch, or the polling agent stays on the fast cadence for nobody.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import * as hub from '../api/hub.js';
import { describeError } from '../api/http.js';
import {
  SnmpVersion,
  SynologyPlatform,
  type DiscoveredDeviceKind,
  type PollingAgentDto,
  type SnmpCredentials,
  type SnmpCredentialShape,
  type SnmpMonitorDto,
  type SynologyCredentials
} from '../contracts.gen.js';
import { formatBytes, formatUptime, isSynologyKind, kindLabel, MONITOR_KINDS, volumeTone } from '../discovery.js';
import { join, localDateTime } from '../format.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { SnmpForm } from '../ui/SnmpForm.js';
import { StatusLine } from '../ui/StatusLine.js';
import { UsageBar } from '../ui/UsageBar.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  passwordBox,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../ui/layout.js';
import { fillMetricGroups } from './sections/metric-groups.js';
import { monitorPerformanceSection } from './sections/monitor-performance.js';
import type { CrmPage, PageContext } from './page.js';

const KEEP_POLLER = 'Keep the stored poller (it is not in this list)';
const PLATFORMS = ['DiskStation (DSM)', 'Router (SRM)'] as const;

function whole(textValue: string): number | null {
  return /^\d+$/.test(textValue.trim()) ? Number(textValue.trim()) : null;
}

export function monitorDetailPage(): CrmPage {
  let release: (() => Promise<void>) | null = null;

  return {
    mount(host: Container, ctx: PageContext) {
      const monitorId = Number(ctx.params.id);
      const { view, content } = scrollPage();
      host.controls.add(view);

      let monitor: SnmpMonitorDto | null = null;
      let agents: PollingAgentDto[] = [];
      let pollerKept = false;

      // ---- header ----
      const backButton = button('← Network', () => {
        if (monitor) ctx.go(`/network/${monitor.customerId}`);
      });
      const title = h1('Monitored device');
      const subtitle = muted('');
      const onlineChip = chip('', 'neutral');
      const lastPolled = muted('');
      const pageStatus = new StatusLine();
      const headingRow = buttonRow({ spacing: GAP.sm, align: 'center' });
      headingRow.controls.add(onlineChip);
      headingRow.controls.add(lastPolled);
      content.controls.add(
        stackOf(vstack({ spacing: GAP.sm }), stackOf(buttonRow(), backButton), title, subtitle, headingRow, pageStatus.label)
      );

      // ---- banners ----
      const agentBanner = card({ tint: 'info', visible: false });
      const agentText = text('');
      agentBanner.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          agentText,
          stackOf(buttonRow(), button('Open device →', () => {
            if (monitor?.agentDeviceId) ctx.go(`/devices/${monitor.agentDeviceId}`);
          }))
        )
      );
      const updateBanner = card({ tint: 'warn', visible: false });
      const updateText = text('', { font: FONT.bold });
      updateBanner.controls.add(
        stackOf(
          vstack({ spacing: GAP.xs }),
          updateText,
          muted("Install it from the device's own web interface - the CRM reports Synology updates but does not apply them.")
        )
      );

      // ---- Synology ----
      const synologyCard = card({ visible: false });
      const identity = vstack({ spacing: GAP.xs });
      const volumes = vstack({ spacing: GAP.sm });
      const disks = vstack({ spacing: GAP.xs });
      const volumesHeading = text('Storage', { font: FONT.bold });
      const disksHeading = text('Drives', { font: FONT.bold });
      const inventoryLine = muted('');
      const inventoryError = muted('', 'danger');
      const snmpOnlyHint = muted(
        'Enrolled over SNMP only. Add a DSM/SRM admin account in the settings below to also see the exact firmware build, storage detail and pending updates.'
      );
      synologyCard.controls.add(
        stackOf(vstack({ spacing: GAP.md }), h2('Synology'), identity, volumesHeading, volumes, disksHeading, disks, inventoryLine, inventoryError, snmpOnlyHint)
      );

      // ---- performance + readings ----
      const performance = monitorPerformanceSection({
        desktop: ctx.desktop,
        dispose: ctx.dispose,
        cancelled: () => ctx.cancelled(),
        relayout: () => relayout(content)
      });
      release = () => performance.detach();
      const groupHost = vstack({ spacing: GAP.md });
      const noMetrics = muted('No metrics yet - waiting for the first poll.');

      // ---- actions + edit ----
      const editButton = button('Edit settings', () => toggleEdit());
      const removeButton = button('Stop monitoring', () => void remove());

      const nameBox = textBox();
      const addressBox = textBox();
      const kindPicker = comboBox<string>(MONITOR_KINDS.map(kindLabel));
      const pollBox = textBox();
      const pollerPicker = comboBox<string>([]);
      const snmp = new SnmpForm();
      snmp.showSecrets(false);
      const replaceSecrets = wrappingCheck('Replace the SNMP passwords');
      replaceSecrets.on('change', () => {
        snmp.showSecrets(replaceSecrets.checked);
        relayout(editCard);
      });

      const platformPicker = comboBox<string>(PLATFORMS);
      const inventoryBox = textBox();
      const apiUser = textBox();
      const apiPort = textBox();
      const https = wrappingCheck('HTTPS');
      const untrusted = wrappingCheck('Accept a self-signed certificate', true);
      const replaceApiPassword = wrappingCheck('Replace the admin password');
      const apiPassword = passwordBox();
      const apiPasswordField = field('Password', apiPassword);
      apiPasswordField.visible = false;
      replaceApiPassword.on('change', () => {
        apiPasswordField.visible = replaceApiPassword.checked;
        relayout(editCard);
      });
      const synologyEdit = stackOf(
        vstack({ spacing: GAP.md }),
        h2('Synology'),
        fieldRow(field('Platform', platformPicker), field('Inventory every (minutes)', inventoryBox)),
        text('DSM / SRM admin account', { font: FONT.bold }),
        muted('Optional. It is what supplies the exact firmware build, disk sizes and pending updates. Clear the user name to go back to SNMP only.'),
        fieldRow(field('User', apiUser), field('Web port (0 = the platform default)', apiPort)),
        checkLine(https),
        checkLine(untrusted),
        checkLine(replaceApiPassword),
        apiPasswordField
      );
      kindPicker.on('selectionchanged', () => {
        synologyEdit.visible = isSynologyKind(selectedKind());
        relayout(editCard);
      });

      const snmpVerdict = muted('');
      const apiVerdict = muted('');
      snmpVerdict.visible = apiVerdict.visible = false;
      const editStatus = new StatusLine();
      const testButton = button('Test connection', () => void test());
      const saveButton = button('Save', () => void save(), true);
      const editCard = card({ visible: false });
      editCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Monitor settings'),
          fieldRow(field('Name', nameBox), field('Address', addressBox)),
          fieldRow(field('Device type', kindPicker), field('Poll every (seconds)', pollBox)),
          field('Polled by', pollerPicker),
          muted('The device type selects which metrics are collected, and the poller has to be an agent on the same network.'),
          h2('SNMP'),
          snmp.panel,
          checkLine(replaceSecrets),
          muted('Leave this off to keep the stored community string and passwords - everything above can still be changed without them.'),
          synologyEdit,
          stackOf(buttonRow(), testButton, saveButton),
          snmpVerdict,
          apiVerdict,
          editStatus.label
        )
      );

      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          agentBanner,
          updateBanner,
          synologyCard,
          h2('Performance'),
          performance.panel,
          h2('Latest readings'),
          groupHost,
          noMetrics,
          stackOf(buttonRow(), editButton, removeButton),
          editCard
        )
      );

      // =================================================================
      //  Showing a monitor
      // =================================================================

      function show(m: SnmpMonitorDto): void {
        monitor = m;
        title.text = m.displayName;
        subtitle.text = join([m.kindLabel, m.ipAddress, `SNMP ${m.snmpVersionLabel}`]);
        onlineChip.text = m.statusLabel || (m.online ? 'Online' : 'Unreachable');
        onlineChip.tone = m.isHealthy ? 'ok' : m.isUnhealthy ? 'danger' : 'neutral';
        lastPolled.text = m.lastPolledUtc ? `Last polled ${localDateTime(m.lastPolledUtc)}` : 'Never polled yet';

        agentBanner.visible = m.hasAgent;
        agentText.text = m.hasAgent
          ? `This machine runs the agent and is enrolled in the fleet as “${m.agentDeviceName}”. It is listed there rather than under monitored devices; SNMP is one of its channels.`
          : '';

        const s = m.synology;
        updateBanner.visible = s?.updateAvailable === true;
        updateText.text = s?.availableVersion ? `Update available: ${s.availableVersion}` : 'A firmware update is available on the device.';

        synologyCard.visible = m.isSynology;
        identity.controls.clear();
        if (s) {
          const facts: [string, string][] = [
            ['Model', s.model || 'Unknown model'],
            ['Firmware', s.firmwareVersion],
            ['Serial', s.serialNumber],
            ['Hostname', s.hostname],
            ['Uptime', formatUptime(s.uptimeSeconds)],
            ['Health', join([s.systemStatus || null, s.temperatureC > 0 ? `${s.temperatureC} °C` : null])]
          ];
          for (const [label, fact] of facts) {
            if (!fact) continue;
            const row = splitRow({ spacing: GAP.md, align: 'start' });
            row.controls.add(value(label, { role: 'muted', width: 110, autoWidth: false }));
            fillWith(row, text(fact));
            identity.controls.add(row);
          }
        }
        volumes.controls.clear();
        for (const v of s?.volumes ?? []) {
          const heading = buttonRow({ spacing: GAP.sm, align: 'center' });
          heading.controls.add(value(v.name, { font: FONT.bold }));
          heading.controls.add(chip(v.status || 'Unknown', v.unhealthy ? 'danger' : 'ok'));
          const detail =
            v.totalBytes > 0
              ? join([
                  `${formatBytes(v.usedBytes)} of ${formatBytes(v.totalBytes)} used (${v.usedPercent}%)`,
                  `${formatBytes(v.totalBytes - v.usedBytes)} free`,
                  v.raidType || null
                ])
              : v.raidType;
          const rowStack = stackOf(vstack({ spacing: GAP.xs }), heading);
          if (v.totalBytes > 0) rowStack.controls.add(new UsageBar({ percent: v.usedPercent, tone: volumeTone(v.usedPercent) }));
          if (detail) rowStack.controls.add(muted(detail));
          volumes.controls.add(rowStack);
        }
        disks.controls.clear();
        for (const d of s?.disks ?? []) {
          const row = buttonRow({ spacing: GAP.sm, align: 'center' });
          row.controls.add(value(d.name, { font: FONT.bold }));
          row.controls.add(muted(d.model || '—'));
          row.controls.add(chip(d.status || 'Unknown', d.unhealthy ? 'danger' : 'ok'));
          const detail = join([d.sizeBytes > 0 ? formatBytes(d.sizeBytes) : null, d.type || null, d.temperatureC > 0 ? `${d.temperatureC} °C` : null]);
          if (detail) row.controls.add(muted(detail));
          disks.controls.add(row);
        }
        volumesHeading.visible = (s?.volumes.length ?? 0) > 0;
        disksHeading.visible = (s?.disks.length ?? 0) > 0;
        // When the inventory was collected and over which channels - and, when the last attempt
        // failed, why. A stale inventory otherwise looks exactly like a current one.
        inventoryLine.text = !m.isSynology
          ? ''
          : m.lastInventoryUtc
            ? `Inventoried ${localDateTime(m.lastInventoryUtc)}${s?.sources ? ` via ${s.sources}` : ''}.`
            : 'Waiting for the first inventory from the on-site agent…';
        inventoryError.text = m.inventoryError ?? '';
        inventoryError.visible = inventoryError.text.length > 0;
        snmpOnlyHint.visible = m.isSynology && !m.hasApiCredentials;

        fillMetricGroups(groupHost, m.latestMetrics ?? []);
        noMetrics.visible = (m.latestMetrics ?? []).length === 0;
        relayout(content);
      }

      async function load(): Promise<void> {
        try {
          const m = await crm.getMonitor(monitorId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          show(m);
          // Idempotent for the same monitor - `MonitorChanged` re-reads on every poll of every
          // monitor this customer has, and re-watching each time would be a hub round trip a poll.
          await performance.attach(m);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error('Could not load this monitor: ' + describeError(error));
        }
      }

      // =================================================================
      //  Editing
      // =================================================================

      function selectedKind(): DiscoveredDeviceKind {
        return MONITOR_KINDS[kindPicker.selectedIndex] ?? MONITOR_KINDS[MONITOR_KINDS.length - 1]!;
      }

      function selectedPoller(): number | null {
        const index = pollerPicker.selectedIndex - (pollerKept ? 1 : 0);
        return index >= 0 ? agents[index]?.deviceId ?? null : null;
      }

      function toggleEdit(): void {
        editCard.visible = !editCard.visible;
        if (editCard.visible) void seed();
        relayout(content);
      }

      async function seed(): Promise<void> {
        const m = monitor;
        if (!m) return;
        nameBox.text = m.displayName;
        addressBox.text = m.ipAddress;
        pollBox.text = String(m.pollSeconds);
        const kindIndex = MONITOR_KINDS.indexOf(m.kind);
        kindPicker.selectedIndex = kindIndex >= 0 ? kindIndex : MONITOR_KINDS.length - 1;
        synologyEdit.visible = isSynologyKind(selectedKind());
        snmp.seed(m.credentials);
        replaceSecrets.checked = false;
        replaceApiPassword.checked = false;
        apiPassword.text = '';
        platformPicker.selectedIndex = m.synologyPlatform === SynologyPlatform.Srm ? 1 : 0;
        inventoryBox.text = String(Math.max(1, Math.round(m.inventorySeconds / 60)));
        apiUser.text = m.synologyApi?.username ?? '';
        apiPort.text = String(m.synologyApi?.port ?? 0);
        https.checked = m.synologyApi?.useHttps ?? false;
        untrusted.checked = m.synologyApi?.allowUntrustedCertificate ?? true;
        // A verdict from the last time the panel was open describes settings that may be gone.
        snmpVerdict.visible = apiVerdict.visible = false;
        editStatus.clear();
        try {
          agents = await crm.getPollingAgents(m.customerId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          const current = agents.findIndex(a => a.deviceId === m.discoveredByDeviceId);
          pollerKept = current < 0;
          pollerPicker.items = [...(pollerKept ? [KEEP_POLLER] : []), ...agents.map(a => a.label || a.name)];
          pollerPicker.selectedIndex = pollerKept ? 0 : current;
        } catch (error) {
          if (!ctx.cancelled()) editStatus.error('Could not load the polling agents: ' + describeError(error));
        }
        relayout(content);
      }

      /** One builder for Save and Test, so a probe cannot pass on settings the save would store differently. */
      function credentials(): { credentials: SnmpCredentials | null; shape: SnmpCredentialShape | null } | string {
        if (replaceSecrets.checked) {
          const built = snmp.build();
          return built.ok ? { credentials: built.credentials, shape: null } : built.error;
        }
        const shape = snmp.buildShape();
        if (shape.version === SnmpVersion.V3 && !shape.securityName) return 'Enter the SNMPv3 user name.';
        return { credentials: null, shape };
      }

      /**
       * The Web API half: an empty user clears the login (back to SNMP-only); a user with no password
       * typed keeps the stored password, so the port or HTTPS can be fixed - or tested - without it.
       */
      function apiCredentials(): SynologyCredentials | null | string {
        if (!isSynologyKind(selectedKind())) return null;
        const port = whole(apiPort.text);
        if (port === null || port > 65535) return 'The web port must be a whole number (0 for the default).';
        const password = replaceApiPassword.checked ? apiPassword.text : '';
        return {
          username: apiUser.text.trim(),
          password,
          port,
          useHttps: https.checked,
          allowUntrustedCertificate: untrusted.checked,
          hasLogin: apiUser.text.trim().length > 0 && password.length > 0
        };
      }

      function selectedPlatform(): SynologyPlatform | null {
        return isSynologyKind(selectedKind()) ? (platformPicker.selectedIndex === 1 ? SynologyPlatform.Srm : SynologyPlatform.Dsm) : null;
      }

      function setBusy(busy: boolean): void {
        testButton.enabled = saveButton.enabled = !busy;
      }

      async function test(): Promise<void> {
        if (!monitor) return;
        const creds = credentials();
        if (typeof creds === 'string') return editStatus.error(creds);
        const api = apiCredentials();
        if (typeof api === 'string') return editStatus.error(api);
        setBusy(true);
        editStatus.busy('Probing…');
        try {
          const result = await crm.testMonitor(monitorId, {
            credentials: creds.credentials,
            credentialShape: creds.shape,
            ipAddress: addressBox.text.trim(),
            kind: selectedKind(),
            pollingDeviceId: selectedPoller(),
            synologyPlatform: selectedPlatform(),
            synologyApi: api
          });
          if (ctx.cancelled()) return;
          snmpVerdict.text =
            (result.snmpOk ? 'SNMP OK — ' : 'SNMP failed — ') + result.snmpMessage + (result.detail ? ` (${result.detail})` : '');
          snmpVerdict.role = result.snmpOk ? 'muted' : 'danger';
          snmpVerdict.visible = true;
          // Only claim a second channel when there was one to try.
          apiVerdict.text = result.apiChecked ? (result.apiOk ? 'Web API OK — ' : 'Web API failed — ') + result.apiMessage : '';
          apiVerdict.role = result.apiChecked && !result.apiOk ? 'danger' : 'muted';
          apiVerdict.visible = apiVerdict.text.length > 0;
          // Points at the reason above rather than blaming the settings: an offline agent, or an
          // unreachable CRM, is not a credential problem.
          if (result.anyOk) editStatus.ok('The device answered - these settings work.');
          else editStatus.error('No answer - see the reason above. Nothing has been saved.');
        } catch (error) {
          if (!ctx.cancelled()) editStatus.error(describeError(error));
        } finally {
          setBusy(false);
          relayout(editCard);
        }
      }

      async function save(): Promise<void> {
        if (!monitor) return;
        const poll = whole(pollBox.text);
        if (poll === null || poll <= 0) return editStatus.error('Poll every must be a whole number of seconds.');
        const creds = credentials();
        if (typeof creds === 'string') return editStatus.error(creds);
        const api = apiCredentials();
        if (typeof api === 'string') return editStatus.error(api);
        let inventorySeconds: number | null = null;
        if (isSynologyKind(selectedKind())) {
          const minutes = whole(inventoryBox.text);
          if (minutes === null || minutes <= 0) return editStatus.error('Inventory every must be a whole number of minutes.');
          inventorySeconds = minutes * 60;
        }
        setBusy(true);
        editStatus.busy('Saving…');
        try {
          await crm.updateMonitor(monitorId, {
            displayName: nameBox.text.trim(),
            pollSeconds: poll,
            credentials: creds.credentials,
            credentialShape: creds.shape,
            ipAddress: addressBox.text.trim(),
            kind: selectedKind(),
            pollingDeviceId: selectedPoller(),
            synologyPlatform: selectedPlatform(),
            inventorySeconds,
            synologyApi: api
          });
          if (ctx.cancelled()) return;
          snmp.clearPasswords();
          apiPassword.text = '';
          editCard.visible = false;
          pageStatus.ok('Monitor settings saved.');
          await load();
        } catch (error) {
          if (!ctx.cancelled()) editStatus.error(describeError(error));
        } finally {
          setBusy(false);
          relayout(content);
        }
      }

      async function remove(): Promise<void> {
        if (!monitor) return;
        const m = monitor;
        const yes = await confirm(ctx.desktop, {
          title: 'Stop monitoring',
          message: `Stop monitoring ${m.displayName}? Its stored readings and maintenance history go with it, and the polling agent stops asking. The device itself is untouched.`
        });
        if (!yes || ctx.cancelled()) return;
        try {
          await crm.removeMonitor(m.id);
          if (ctx.cancelled()) return;
          await performance.detach();
          ctx.go(`/network/${m.customerId}`, { replace: true });
        } catch (error) {
          if (!ctx.cancelled()) pageStatus.error(describeError(error));
        }
      }

      void (async () => {
        await load();
        // Assigned inside `load`, which TypeScript cannot see from here.
        const loaded = monitor as SnmpMonitorDto | null;
        if (ctx.cancelled() || !loaded) return;
        if (await hub.ensureConnected()) {
          if (ctx.cancelled()) return;
          const customerId = loaded.customerId;
          ctx.dispose.add(
            hub.on('MonitorChanged', changed => {
              if (changed === customerId) void load();
            })
          );
        }
      })();
    },
    unmount() {
      void release?.();
      release = null;
    }
  };
}
